// Разбор того, что человек напечатал в поле почты.
//
// Зачем это отдельным модулем, а не двумя строчками в `email-step`: контракт
// (`EmailSchema` → `z.email()`) принимает только ASCII, а мы обещаем «все
// аудитории и страны». Адрес `jürgen@bücher.de` — валидный адрес с IDN-доменом,
// и отвергать его сообщением «неверный адрес» неправильно дважды: он верный, и
// человек не понимает, что именно исправлять.
//
// Домен приводится к punycode средствами платформы: `new URL()` выполняет IDNA
// ToASCII по тем же таблицам, что и браузерная адресная строка. Своей таблицы
// Unicode мы не держим — она устаревает вместе со стандартом.
//
// Не-ASCII в ЛОКАЛЬНОЙ части (до `@`) — это EAI (RFC 6531), и её нельзя
// «сконвертировать»: доставка такого письма зависит от того, поддерживает ли
// SMTPUTF8 каждый узел на пути. Пока мы этого не гарантируем — говорим прямо.

/** Что удалось понять из введённой строки. */
export type EmailInputParse =
	| { readonly state: "ok"; readonly value: string; readonly converted: boolean }
	| { readonly state: "unicode-local" }
	| { readonly state: "invalid" };

const ASCII_ONLY = /^[\x20-\x7E]*$/;
/** Символы, из-за которых `new URL` разберёт «домен» как путь или порт. */
const NOT_A_HOST = /[\s/?#:@[\]\\]/;

/**
 * Привести домен к punycode. Возвращает `undefined`, если это вообще не домен.
 */
function domainToAscii(domain: string): string | undefined {
	if (domain.length === 0 || NOT_A_HOST.test(domain)) return undefined;
	try {
		const host = new URL(`http://${domain}`).hostname;
		return host.length > 0 ? host : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Нормализовать введённый адрес под контракт.
 *
 * `isValid` — проверка контрактной схемой, передаётся снаружи: в `lib/` не
 * должно быть второй копии правил валидации почты.
 */
export function parseEmailInput(raw: string, isValid: (value: string) => boolean): EmailInputParse {
	const trimmed = raw.trim();
	if (isValid(trimmed)) return { state: "ok", value: trimmed, converted: false };

	const at = trimmed.lastIndexOf("@");
	if (at <= 0 || at === trimmed.length - 1) return { state: "invalid" };

	const local = trimmed.slice(0, at);
	const domain = trimmed.slice(at + 1);

	if (!ASCII_ONLY.test(local)) return { state: "unicode-local" };

	const ascii = domainToAscii(domain);
	if (ascii === undefined) return { state: "invalid" };

	const candidate = `${local}@${ascii}`;
	if (!isValid(candidate)) return { state: "invalid" };
	return { state: "ok", value: candidate, converted: candidate !== trimmed };
}
